import { useEffect, useRef, useState } from 'react'

import { type OrganizationVisibility } from '@ankor-io/common/types/account'
import { Button, EmptyState, Heading, Loading, Text } from '@ankor-io/ui-components'

import OrganizationsFiltersComponent, {
  type OrganizationsFilters,
} from '@/components/organizations/organizations-filters/organizations-filters'
import { OrganizationsTable } from '@/components/organizations/organizations-table'
import { MIN_LOADING_DISPLAY_MS } from '@/constants/ui'
import { useMinimumDisplayTime } from '@/hooks/useMinimumDisplayTime'
import { useInfiniteOrganizationsQuery } from '@/queries'

const ORGANIZATIONS_PER_PAGE = 20

export function Organizations() {
  const [organizationsFilters, setOrganizationsFilters] = useState<OrganizationsFilters>({
    organizationName: '',
    organizationId: '',
    visibility: '',
    vesselName: '',
  })

  const { isPending, isError, data, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteOrganizationsQuery(
      {
        visibility: organizationsFilters.visibility
          ? [organizationsFilters.visibility as OrganizationVisibility]
          : undefined,
        organizationName: organizationsFilters.organizationName,
        organizationId: organizationsFilters.organizationId,
        vesselName: organizationsFilters.vesselName,
      },
      ORGANIZATIONS_PER_PAGE,
    )

  // Flatten all pages into a single array
  const organizations = data?.pages.flat() ?? []

  const fetchingMore = isPending

  // No need to show loader if we have cached data
  // const shouldShowLoader = organizations.length === 0 && isPending
  const showLoader = useMinimumDisplayTime(isPending, MIN_LOADING_DISPLAY_MS)

  // Ref for sentinel element
  const sentinelRef = useRef<HTMLDivElement>(null)

  const handleFiltersChanged = (filters: OrganizationsFilters) => {
    setOrganizationsFilters(filters)
  }

  const noOrgs = organizations.length === 0
  const showNoOrgs = noOrgs && !isError && !isPending
  const hasActiveFilters =
    organizationsFilters.organizationName !== '' ||
    organizationsFilters.organizationId !== '' ||
    organizationsFilters.visibility !== '' ||
    organizationsFilters.vesselName !== ''

  // Error toast is handled globally by QueryProvider
  // Component still handles error UI state (error display below)

  // Set up intersection observer for infinite scroll
  // Only runs when component is fully rendered (not in loading/error/empty states)
  useEffect(() => {
    // Skip if we're in loading/error/empty states
    if (showLoader || isPending || error || showNoOrgs) {
      return
    }

    const sentinel = sentinelRef.current
    if (!sentinel) {
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0]
        if (first.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      {
        threshold: 0.1,
        rootMargin: '100px',
      },
    )

    observer.observe(sentinel)

    return () => {
      observer.disconnect()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, showLoader, isPending, error, showNoOrgs])

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <Text className="text-red-600">{error.message || 'Failed to load organizations. Please try again.'}</Text>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex md:items-center justify-between gap-2 flex-col md:flex-row">
        <div>
          <Heading level={3}>Organizations</Heading>
          <Text className="mt-2 text-gray-600 dark:text-gray-400">Manage all organizations in the system</Text>
        </div>

        <Button
          className="w-full md:w-auto"
          color="primary"
          disabled
        >
          New Organization
        </Button>
      </div>
      <OrganizationsFiltersComponent
        filters={organizationsFilters}
        onFiltersChanged={handleFiltersChanged}
        isApplyingFilters={fetchingMore}
      />

      {showNoOrgs ? (
        <EmptyState
          heading={hasActiveFilters ? 'No matching organizations' : 'No organizations found'}
          subheading={
            hasActiveFilters
              ? 'Try adjusting your filters to find what you are looking for.'
              : 'Get started by creating a new organization.'
          }
        />
      ) : (
        <>
          <OrganizationsTable
            organizations={organizations}
            isLoading={showLoader}
          />

          {/* Sentinel element for infinite scroll */}
          <div
            ref={sentinelRef}
            className="h-20 flex items-center justify-center"
          >
            {isFetchingNextPage && <Loading variant="dots" />}
          </div>
        </>
      )}
    </div>
  )
}

export default Organizations
